// IND-038: sección «EPDM por línea» de la vista Desperdicio. El total de EPDM es
// el dato bueno: un mes cuyas líneas no lo suman se ve atenuado y no cuenta.
import { useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { WasteEpdmLinesMonth, WasteEpdmLinesView } from '../services/industrialCostService';
import { esNumberFormat } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import { epdmLineAverage, epdmLineCell, MONTH_SHORT } from '../utils/wasteControl';
import type { EpdmLineMetric } from '../utils/wasteControl';
import SortableHeaderCell from '../components/SortableHeaderCell';
import { DetailError, DetailLoading } from './IndustrialCostMaterialDetailSections';

const MUTED = '#94a3b8';
const DIM = '#64748b';
const AMBER = '#fbbf24';
const OK = '#86efac';
const HEADER_BG = '#151f30';
const CONTROL_BG = 'rgba(148,163,184,0.05)';
const PANEL = {
  bgcolor: '#151f30',
  border: '1px solid rgba(148,163,184,0.15)',
  borderRadius: 3,
  boxShadow: 'none',
};
const kg = esNumberFormat({ maximumFractionDigits: 0 });
const decimal = esNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtKg(value: number | null): string {
  return value === null ? '—' : kg.format(value);
}

function fmtMetric(value: number | null, metric: EpdmLineMetric): string {
  if (value === null) return '—';
  return metric === 'kg' ? kg.format(value) : metric === 'pct' ? `${decimal.format(value)} %` : decimal.format(value);
}

function cellText(month: WasteEpdmLinesMonth, key: string, metric: EpdmLineMetric): string {
  if ((month.values[key] ?? null) === null) return 'sin dato';
  return fmtMetric(epdmLineCell(month, key, metric), metric);
}

const OK_TEXT: Record<WasteEpdmLinesMonth['status'], { text: string; color: string }> = {
  ok: { text: 'Sí', color: OK },
  mismatch: { text: 'No', color: AMBER },
  empty: { text: 'sin desglose', color: DIM },
};

export default function IndustrialCostWasteEpdmLinesSection({ query }: { query: UseQueryResult<WasteEpdmLinesView> }) {
  if (query.isError) {
    return <DetailError error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />;
  }
  if (!query.data) return <DetailLoading height={200} />;
  return <LinesTable view={query.data} />;
}

function LinesTable({ view }: { view: WasteEpdmLinesView }) {
  const [metric, setMetric] = useState<EpdmLineMetric>('kg');
  const sort = useTableSort<string>(null, 'desc');
  const value = (line: WasteEpdmLinesView['lines'][number]): SortValue =>
    sort.key === 'line'
      ? line.name
      : sort.key === 'media'
        ? epdmLineAverage(view, line.key, metric)
        : epdmLineCell(view.months.find((month) => month.month === Number(sort.key)), line.key, metric);
  const rows = sort.key ? sortRows(view.lines, value, sort.direction) : view.lines;
  const control = { bgcolor: CONTROL_BG, fontSize: 11.5, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };

  return (
    <Paper sx={{ ...PANEL, p: 1.5 }}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 14 }}>EPDM por línea</Typography>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>
            El dato bueno es el total de EPDM; los meses en que las líneas no suman ese total salen atenuados y no cuentan.
          </Typography>
        </Stack>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={metric}
          onChange={(_, next: EpdmLineMetric | null) => next && setMetric(next)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 800, px: 1, py: 0.25 } }}
        >
          <ToggleButton value="kg">kg</ToggleButton>
          <ToggleButton value="pct">% del EPDM del mes</ToggleButton>
          <ToggleButton value="per100">kg por 100 m</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {view.lines.length === 0 ? (
        <Typography sx={{ color: MUTED, fontSize: 13 }}>No hay líneas de la familia EPDM en la configuración de planta.</Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.75, fontSize: 12 } }}>
              <TableHead>
                <TableRow>
                  <SortableHeaderCell label="Línea" columnKey="line" sort={sort} firstDirection="asc" sx={{ bgcolor: HEADER_BG }} />
                  {view.months.map((month) => (
                    <SortableHeaderCell
                      key={month.month}
                      label={`${MONTH_SHORT[month.month - 1]}${month.status === 'mismatch' ? ' ⚠' : ''}`}
                      columnKey={String(month.month)}
                      sort={sort}
                      align="right"
                      sx={{ bgcolor: HEADER_BG, whiteSpace: 'nowrap', color: month.status === 'mismatch' ? AMBER : undefined }}
                    />
                  ))}
                  <SortableHeaderCell label="Media*" columnKey="media" sort={sort} align="right" sx={{ bgcolor: HEADER_BG }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((line) => (
                  <TableRow key={line.key}>
                    <TableCell sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{line.name}</TableCell>
                    {view.months.map((month) => {
                      const mismatch = month.status === 'mismatch';
                      return (
                        <TableCell
                          key={month.month}
                          align="right"
                          sx={{
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                            color: mismatch ? DIM : (month.values[line.key] ?? null) === null ? MUTED : undefined,
                            fontStyle: mismatch ? 'italic' : undefined,
                          }}
                        >
                          {cellText(month, line.key, metric)}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ fontWeight: 900, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtMetric(epdmLineAverage(view, line.key, metric), metric)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ ...control, color: '#cbd5e1' }}>Suma de líneas</TableCell>
                  {view.months.map((month) => (
                    <TableCell key={month.month} align="right" sx={{ ...control, color: month.status === 'mismatch' ? AMBER : '#cbd5e1' }}>
                      {fmtKg(month.lines_kg)}
                    </TableCell>
                  ))}
                  <TableCell sx={control} />
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...control, fontWeight: 800 }}>Total EPDM</TableCell>
                  {view.months.map((month) => (
                    <TableCell key={month.month} align="right" sx={{ ...control, fontWeight: 800 }}>{fmtKg(month.total_kg)}</TableCell>
                  ))}
                  <TableCell sx={control} />
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...control, color: '#cbd5e1' }}>¿Cuadra?</TableCell>
                  {view.months.map((month) => (
                    <TableCell key={month.month} align="right" sx={{ ...control, fontWeight: 800, color: OK_TEXT[month.status].color }}>
                      {OK_TEXT[month.status].text}
                    </TableCell>
                  ))}
                  <TableCell sx={control} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={{ color: MUTED, fontSize: 11, mt: 0.75 }}>
            * Media solo de los meses que cuadran; en kg por 100 m, total kg ÷ total metros. Las filas de control van en kg. «kg por 100 m» usa los metros de cada línea de los partes de producción.
          </Typography>
        </>
      )}
    </Paper>
  );
}
